import React, { useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { Label, Switch, Textarea } from '../../../components/ui';
import { ChevronDownIcon, CheckCircleFilledIcon, XCircleFilledIcon } from '../../../components/ui/Icons';

// ChevronUpIcon - simple SVG component
const ChevronUpIcon = ({ className = "h-5 w-5", ...props }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24" {...props}>
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
  </svg>
);

/**
 * DeviationImpactAssessmentAccordion - Accordion component for Impact Assessment section
 * Groups all impact assessment fields in a collapsible accordion, similar to Possible Causes
 */
const DeviationImpactAssessmentAccordion = ({ 
  formData,
  setFormData,
  config,
  fields,
  isEditMode = false,
  selectedRecord = null
}) => {
  const { theme } = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  
  // Determine if we're in view mode
  const isViewMode = selectedRecord && !isEditMode;
  const recordData = isViewMode ? selectedRecord : formData;

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  // Group fields by impact type
  const impactGroups = [
    {
      name: 'customerImpact',
      label: 'Notification of Customer',
      switchField: 'customerImpact',
      noteField: 'customerImpactNote'
    },
    {
      name: 'impactOnProduct',
      label: 'Impact on Product',
      switchField: 'impactOnProduct',
      noteField: 'impactOnProductNote'
    },
    {
      name: 'impactOnOtherBatches',
      label: 'Impact on Other Batches',
      switchField: 'impactOnOtherBatches',
      noteField: 'impactOnOtherBatchesNote'
    },
    {
      name: 'impactOnOtherProducts',
      label: 'Impact on Other Products',
      switchField: 'impactOnOtherProducts',
      noteField: 'impactOnOtherProductsNote'
    },
    {
      name: 'impactOnOtherSystems',
      label: 'Impact on Other Systems',
      switchField: 'impactOnOtherSystems',
      noteField: 'impactOnOtherSystemsNote'
    }
  ];

  return (
    <div className={cn("border rounded-lg", theme.border.light, theme.colors.background)}>
      {/* Accordion Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          "w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors",
          theme.text.primary
        )}
      >
        <div className="flex items-center gap-2">
          <span className="text-lg">⚠️</span>
          <span className="font-semibold">Impact Assessment</span>
          {(() => {
            const activeImpacts = impactGroups.filter(group => recordData[group.switchField]).length;
            if (activeImpacts > 0) {
              return (
                <span className={cn(
                  "px-2 py-0.5 rounded-full text-xs font-medium",
                  theme.colors.primary,
                  theme.text.inverse
                )}>
                  {activeImpacts} active
                </span>
              );
            }
            return null;
          })()}
        </div>
        {isExpanded ? (
          <ChevronUpIcon className="w-5 h-5" />
        ) : (
          <ChevronDownIcon className="w-5 h-5" />
        )}
      </button>

      {/* Accordion Content */}
      {isExpanded && (
        <div className={cn("p-4 border-t space-y-6", theme.border.light)}>
          {impactGroups.map((group) => {
            const switchField = fields.find(f => f.name === group.switchField);
            const noteField = fields.find(f => f.name === group.noteField);
            const isActive = recordData[group.switchField] || false;

            return (
              <div
                key={group.name}
                className={cn(
                  "p-4 rounded-lg border space-y-3",
                  theme.border.subtle,
                  isActive ? theme.colors.backgroundAlt : theme.colors.background
                )}
              >
                {/* Switch Field */}
                <div className="flex items-center justify-between">
                  <Label htmlFor={group.switchField}>
                    {group.label}
                  </Label>
                  {isViewMode ? (
                    <span className={cn(
                      "inline-flex items-center gap-1 text-sm",
                      isActive ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"
                    )}>
                      {isActive ? (
                        <>
                          <CheckCircleFilledIcon className="w-4 h-4" />
                          <span>Yes</span>
                        </>
                      ) : (
                        <>
                          <XCircleFilledIcon className="w-4 h-4" />
                          <span>No</span>
                        </>
                      )}
                    </span>
                  ) : (
                    <Switch
                      id={group.switchField}
                      checked={isActive}
                      onChange={(checked, e) => handleFieldChange(group.switchField, checked)}
                      variant="simple"
                    />
                  )}
                </div>

                {/* Note Field - Only show if switch is active */}
                {isActive && noteField && (
                  <div>
                    <Label htmlFor={group.noteField}>
                      {noteField.label}
                    </Label>
                    {isViewMode ? (
                      <div className={cn("text-sm p-2 rounded mt-1 whitespace-pre-wrap", theme.colors.background, theme.text.primary)}>
                        {recordData[group.noteField] || <span className={cn(theme.text.muted)}>—</span>}
                      </div>
                    ) : (
                      <Textarea
                        id={group.noteField}
                        value={recordData[group.noteField] || ''}
                        onChange={(e) => handleFieldChange(group.noteField, e.target.value)}
                        placeholder={noteField.placeholder || `Enter ${group.label.toLowerCase()} details`}
                        rows={noteField.rows || 2}
                        className="mt-1"
                      />
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DeviationImpactAssessmentAccordion;

